import { useUser } from "@clerk/clerk-react";
import { useQuery } from "convex/react";
import { Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  Rss,
  MessageSquare,
  Users,
  UserPlus,
  Sparkles,
  Trophy,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { UpdateMarkdown } from "@/components/UpdateMarkdown";
import { api } from "@/convex";

function getEventThumbnail(title: string): string | null {
  if (title.toLowerCase().includes("energy freedom")) return "/events/energy-freedom.jpg";
  if (title.toLowerCase().includes("dc dinner")) return "/events/dc-dinner.jpg";
  return null;
}

const Dashboard = () => {
  const { user } = useUser();
  const firstName = user?.firstName || "Member";
  const recentUpdates = useQuery(api.updates.list, { limit: 2 });
  const upcomingEvents = useQuery(api.events.list, { limit: 3 });

  const quickLinks = [
    {
      icon: Calendar,
      title: "Upcoming Events",
      description: "View and manage upcoming group events",
      href: "/events",
    },
    {
      icon: Rss,
      title: "Updates",
      description: "Latest news and announcements",
      href: "/feed",
    },
    {
      icon: Sparkles,
      title: "AlexAI Pro",
      description: "Access your private AI tools",
      href: "/ai",
    },
    {
      icon: MessageSquare,
      title: "WhatsApp Chat",
      description: "Join the member conversation",
      href: "/whatsapp",
    },
    {
      icon: Trophy,
      title: "Conference",
      description: "View conference schedule and attendees",
      href: "/conference",
    },
    {
      icon: Users,
      title: "Members",
      description: "Confidential member directory",
      href: "/members",
    },
    {
      icon: UserPlus,
      title: "Refer a Peer",
      description: "Invite qualified executives",
      href: "/refer",
    },
  ];

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-6 py-10 max-w-4xl">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-2xl sm:text-4xl font-semibold text-foreground mb-2 tracking-tight">
            Welcome, {firstName}!
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base tracking-wide">
            Advancing energy freedom and human flourishing
          </p>
        </div>

        {/* What's New Section */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold text-foreground mb-5">What's New</h2>
          <div className="space-y-4">
            {recentUpdates === undefined ? (
              [1, 2].map((i) => (
                <Card key={i} className="bg-card border-border/40">
                  <CardContent className="p-5 space-y-3">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-9 w-24" />
                  </CardContent>
                </Card>
              ))
            ) : (
              recentUpdates.map((update) => (
                <Card key={update._id} className="bg-card border-border/40 overflow-hidden">
                  {update.vimeoId && (
                    <img
                      src={`https://vumbnail.com/${update.vimeoId}.jpg`}
                      alt={update.title}
                      className="w-full h-36 object-cover sm:hidden"
                    />
                  )}
                  <CardContent className="p-5">
                    <div className="flex gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-foreground mb-2 leading-snug">
                          {update.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                          Published {update.date}
                        </p>
                        <div className="text-sm text-muted-foreground mb-4 leading-relaxed prose prose-sm max-w-none">
                          <UpdateMarkdown>{update.excerpt}</UpdateMarkdown>
                        </div>
                        <Button asChild>
                          <Link to="/feed">
                            Read More
                          </Link>
                        </Button>
                      </div>
                      {update.vimeoId && (
                        <div className="hidden sm:flex flex-shrink-0 items-center">
                          <img
                            src={`https://vumbnail.com/${update.vimeoId}.jpg`}
                            alt={update.title}
                            className="w-40 h-32 rounded-lg object-cover"
                          />
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
          <div className="mt-4">
            <Link
              to="/feed"
              className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
            >
              View More →
            </Link>
          </div>
        </section>

        {/* What's Next Section */}
        {upcomingEvents === undefined ? (
          <section className="mb-10">
            <h2 className="text-lg font-semibold text-foreground mb-5">What's Next</h2>
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <Card key={i} className="bg-card border-border/40">
                  <CardContent className="p-5 space-y-3">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-9 w-24" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        ) : upcomingEvents.length > 0 ? (
          <section className="mb-10">
            <h2 className="text-lg font-semibold text-foreground mb-5">What's Next</h2>
            <div className="space-y-4">
              {upcomingEvents.map((event) => (
                <Card
                  key={event._id}
                  data-testid="dashboard-event-card"
                  className="bg-card border-border/40 overflow-hidden"
                >
                  {(event.imageUrl || getEventThumbnail(event.title)) && (
                    <img
                      src={(event.imageUrl || getEventThumbnail(event.title))!}
                      alt={event.title}
                      className="w-full h-36 object-cover sm:hidden"
                    />
                  )}
                  <CardContent className="p-5">
                    <div className="flex gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-foreground mb-2 leading-snug">
                          {event.title}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            {event.dateDisplay}
                          </span>
                          {!event.dateTbd && (
                            <span className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5" />
                              {event.time}
                            </span>
                          )}
                        </div>
                        <Button asChild>
                          <Link to={event.type === "Annual Conference" ? "/conference" : "/events"}>View Details</Link>
                        </Button>
                      </div>
                      {(event.imageUrl || getEventThumbnail(event.title)) && (
                        <div className="hidden sm:flex flex-shrink-0 items-center">
                          <img
                            src={(event.imageUrl || getEventThumbnail(event.title))!}
                            alt={event.title}
                            data-testid="dashboard-event-card-photo"
                            className="w-48 aspect-[5/4] rounded-lg object-cover"
                          />
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="mt-4">
              <Link
                to="/events"
                className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
              >
                View All Events →
              </Link>
            </div>
          </section>
        ) : null}

        {/* Quick Links Section */}
        <section>
          <h2 className="text-lg font-semibold text-foreground mb-5">Quick Links</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {quickLinks.map((link, index) => (
              <Link key={index} to={link.href}>
                <Card className="h-full bg-card border-border/40 hover:border-border/80 hover:shadow-sm transition-all duration-200 cursor-pointer">
                  <CardContent className="p-4">
                    <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center mb-3">
                      <link.icon className="w-5 h-5 text-slate-700" />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground mb-1">
                      {link.title}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {link.description}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
